import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Button,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { PagamentoModalProps, MetodoPagamento, ModalStep, AsaasPaymentResponse } from "./types";
import PixPaymentView from "./PixPaymentView.tsx";
import BoletoPaymentView from "./BoletoPaymentView.tsx";
import CreditCardForm, { CreditCardData } from "./CreditCardForm.tsx";
import SuccessView from "./SuccessView.tsx";
import { usePagamentoPolling } from "../../../hooks/usePagamentoPolling.ts";
import pagamentoService from "../../../services/pagamentosService.ts";

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);

const METODOS: Array<{
  tipo: MetodoPagamento;
  nome: string;
  desc: string;
  icone: string;
  recomendado?: boolean;
}> = [
  {
    tipo: "PIX",
    nome: "PIX",
    desc: "Pagamento instantâneo",
    icone: "\uD83D\uDCF1",
    recomendado: true,
  },
  {
    tipo: "BOLETO",
    nome: "Boleto Bancário",
    desc: "Vencimento em 3 dias úteis",
    icone: "\uD83D\uDCC4",
  },
  {
    tipo: "CARTAO",
    nome: "Cartão de Crédito",
    desc: "Pagamento na hora",
    icone: "\uD83D\uDCB3",
  },
];

const PagamentoModal: React.FC<PagamentoModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  planoNome,
  planoPreco,
  planoCiclo,
  isLoading: externalLoading = false,
  onPaymentComplete,
  initialPaymentData = null,
  avisoPagamentoAteData = null,
}) => {
  const [step, setStep] = useState<ModalStep>("select-method");
  const [metodoSelecionado, setMetodoSelecionado] = useState<MetodoPagamento | null>(null);
  const [paymentData, setPaymentData] = useState<AsaasPaymentResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showCreditCardForm, setShowCreditCardForm] = useState(false);

  const autoCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearAutoClose = () => {
    if (autoCloseTimerRef.current) {
      clearTimeout(autoCloseTimerRef.current);
      autoCloseTimerRef.current = null;
    }
  };

  useEffect(() => {
    return () => clearAutoClose();
  }, []);

  const { pago } = usePagamentoPolling(paymentData?.paymentId || null, step === "payment-display");

  useEffect(() => {
    if (pago) {
      setStep("success");
      clearAutoClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pago]);

  useEffect(() => {
    if (isOpen) {
      if (initialPaymentData) {
        // Cobrança já criada pelo backend (ex.: ajuste proporcional de upgrade) —
        // pula a escolha de método e cai direto na tela de pagamento/sucesso.
        setPaymentData(initialPaymentData);
        setMetodoSelecionado(null);
        setErrorMessage(null);
        setShowCreditCardForm(false);
        setStep(initialPaymentData.status === "PLANO_ALTERADO" ? "success" : "payment-display");
      } else {
        setStep("select-method");
        setMetodoSelecionado(null);
        setPaymentData(null);
        setErrorMessage(null);
        setShowCreditCardForm(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialPaymentData]);

  useEffect(() => {
    if (step === "success") {
      autoCloseTimerRef.current = setTimeout(() => {
        handleClose();
      }, 7000);
      return () => clearAutoClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const handleClose = useCallback(() => {
    clearAutoClose();
    setMetodoSelecionado(null);
    setPaymentData(null);
    setErrorMessage(null);
    setStep("select-method");
    setShowCreditCardForm(false);
    onClose();
  }, [onClose]);

  const handleBackToMethodSelect = useCallback(() => {
    // Cliente está saindo da tela de PIX/Boleto sem pagar — é o único ponto de
    // saída real dessa tela (o "X" fica escondido em payment-display). Avisa o
    // ASAAS pra cancelar a cobrança pendente — best-effort, não bloqueia a UI
    // nem mostra erro caso falhe.
    if (
      paymentData?.subscriptionId &&
      !paymentData.ajusteProporcional &&
      paymentData.billingType !== "CREDIT_CARD"
    ) {
      pagamentoService.cancelarAssinaturaPendente().catch((err) => {
        console.error("Falha ao cancelar assinatura pendente:", err);
      });
    }

    setStep("select-method");
    setMetodoSelecionado(null);
    setPaymentData(null);
    setErrorMessage(null);
    setShowCreditCardForm(false);
  }, [paymentData]);

  const handleMethodSelect = (metodo: MetodoPagamento) => {
    setMetodoSelecionado(metodo);
    if (metodo === "CARTAO") {
      setShowCreditCardForm(true);
    } else {
      handleConfirmPayment(metodo);
    }
  };

  const handleConfirmPayment = async (metodo: MetodoPagamento, creditCard?: CreditCardData) => {
    setIsLoading(true);
    setErrorMessage(null);

    try {
      const result = await onConfirm(metodo, creditCard);
      if (result) {
        setPaymentData(result);

        // ═══════════════════════════════════════════════════
        // PLANO JÁ ALTERADO (troca sem novo pagamento) OU
        // ASSINATURA REATIVADA DENTRO DA CARÊNCIA (sem cobrança agora)
        // ═══════════════════════════════════════════════════
        if (result.status === "PLANO_ALTERADO" || result.status === "ASSINATURA_REATIVADA") {
          setShowCreditCardForm(false);
          setStep("success");
          clearAutoClose();
        } else if (result.billingType === "CREDIT_CARD" &&
                   result.status !== "CONFIRMED" &&
                   result.status !== "RECEIVED" &&
                   result.status !== "PENDING") {
          // Erro no cartão — mostra mensagem de erro
          setErrorMessage(result.creditCardMessage || "Pagamento não aprovado. Tente novamente.");
          setMetodoSelecionado(null);
        } else {
          // ═══════════════════════════════════════════════════
          // PIX, BOLETO e CARTÃO (confirmado/pendente):
          // TODOS vão para payment-display obrigatoriamente.
          // Apenas o usePagamentoPolling pode levar para success.
          // ═══════════════════════════════════════════════════
          setShowCreditCardForm(false);
          setStep("payment-display");
        }

        if (onPaymentComplete) {
          onPaymentComplete(result);
        }
      }
    } catch (error: any) {
      const rawMessage = error.message || "Erro ao processar pagamento";
      const isTimeout =
        rawMessage.includes("Read timed out") ||
        rawMessage.includes("I/O error") ||
        rawMessage.includes("timeout");

      setErrorMessage(
        isTimeout
          ? "O processamento do pagamento demorou mais que o esperado. Por favor, aguarde alguns instantes e verifique seu plano, ou tente novamente."
          : rawMessage
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreditCardSubmit = (data: CreditCardData) => {
    handleConfirmPayment("CARTAO", data);
  };

  const renderStepIndicator = () => {
    if (step === "select-method") return null;
    const steps = [
      { label: "Escolha", active: step === "select-method", done: false },
      { label: "Pagamento", active: step === "payment-display", done: step === "success" },
      { label: "Confirmação", active: step === "success", done: false },
    ];
    return (
      <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mb: 2 }}>
        {steps.map((s, i) => (
          <React.Fragment key={s.label}>
            <Box
              sx={{
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                fontSize: "0.75rem",
                fontWeight: 600,
                bgcolor: s.active ? "var(--blue)" : s.done ? "var(--green)" : "var(--surface-3)",
                color: s.active || s.done ? "#fff" : "var(--ink-faint)",
              }}
            >
              {s.done ? "\u2713" : i + 1} {s.label}
            </Box>
            {i < steps.length - 1 && (
              <Box sx={{ display: "flex", alignItems: "center", color: "var(--border-strong)" }}>
                <ArrowForwardIcon sx={{ fontSize: 14 }} />
              </Box>
            )}
          </React.Fragment>
        ))}
      </Box>
    );
  };

  const renderMetodosSelect = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box
        sx={{
          backgroundColor: "var(--blue-tint)",
          border: "1px solid var(--blue)",
          borderRadius: 2,
          padding: 2,
          textAlign: "center",
        }}
      >
        <Typography sx={{ color: "var(--ink-soft)", fontWeight: 600, fontSize: "0.9rem" }}>
          Plano Selecionado
        </Typography>
        <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: "var(--ink)", mt: 0.5 }}>
          {planoNome}
        </Typography>
        <Typography sx={{ fontSize: "1.5rem", fontWeight: "bold", color: "var(--ink)", mt: 1 }}>
          {formatCurrency(planoPreco)}/{planoCiclo === "YEARLY" ? "ano" : "m\u00eas"}
        </Typography>
      </Box>

      <Typography sx={{ fontWeight: 600, color: "var(--ink)" }}>Selecione o método de pagamento:</Typography>

      {METODOS.map((metodo) => (
        <Box
          key={metodo.tipo}
          onClick={() => handleMethodSelect(metodo.tipo)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            padding: "14px 16px",
            border: "2px solid",
            borderColor: metodoSelecionado === metodo.tipo ? "var(--blue)" : "var(--border)",
            borderRadius: 2,
            opacity: isLoading ? 0.7 : 1,
            backgroundColor: metodoSelecionado === metodo.tipo ? "var(--blue-tint)" : "var(--surface)",
            cursor: isLoading ? "not-allowed" : "pointer",
            transition: "all 0.2s",
            "&:hover": isLoading ? {} : { borderColor: "var(--blue)", backgroundColor: "var(--blue-tint)" },
          }}
        >
          <Box
            sx={{
              width: 20,
              height: 20,
              borderRadius: "50%",
              border: "2px solid",
              borderColor: metodoSelecionado === metodo.tipo ? "var(--blue)" : "var(--ink-faint)",
              backgroundColor: metodoSelecionado === metodo.tipo ? "var(--blue)" : "transparent",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isLoading && metodoSelecionado === metodo.tipo && (
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#fff" }} />
            )}
          </Box>
          <Typography sx={{ fontSize: "1.5rem" }}>{metodo.icone}</Typography>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 500, color: "var(--ink)" }}>{metodo.nome}</Typography>
            <Typography sx={{ fontSize: "0.8rem", color: "var(--ink-soft)" }}>{metodo.desc}</Typography>
          </Box>
          {metodo.recomendado && (
            <Box
              sx={{
                bgcolor: "var(--green)",
                color: "#fff",
                padding: "4px 8px",
                borderRadius: 1,
                fontSize: "0.7rem",
                fontWeight: 600,
                textTransform: "uppercase",
              }}
            >
              Recomendado
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );

  const renderContent = () => {
    if (errorMessage) {
      return (
        <Box>
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage(null)}>
            {errorMessage}
          </Alert>
          <Button variant="outlined" onClick={handleClose} fullWidth>
            Tentar novamente
          </Button>
        </Box>
      );
    }

    if (showCreditCardForm && metodoSelecionado === "CARTAO") {
      return (
        <CreditCardForm
          onSubmit={handleCreditCardSubmit}
          onBack={() => { setShowCreditCardForm(false); setMetodoSelecionado(null); }}
          isLoading={isLoading}
        />
      );
    }

    switch (step) {
      case "select-method":
        return renderMetodosSelect();

      case "payment-display": {
        if (!paymentData) return null;
        let content;
        if (paymentData.billingType === "PIX" && paymentData.pixEncodedImage && paymentData.pixPayload) {
          content = (
            <PixPaymentView
              encodedImage={paymentData.pixEncodedImage}
              payload={paymentData.pixPayload}
              onBack={handleBackToMethodSelect}
            />
          );
        } else if (paymentData.billingType === "BOLETO" && paymentData.identificationField && paymentData.bankSlipUrl) {
          content = (
            <BoletoPaymentView
              identificationField={paymentData.identificationField}
              bankSlipUrl={paymentData.bankSlipUrl}
              onBack={handleBackToMethodSelect}
            />
          );
        } else if (paymentData.billingType === "CREDIT_CARD") {
          const isConfirmed = paymentData.status === "CONFIRMED" || paymentData.status === "RECEIVED";
          content = (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Box sx={{ fontSize: 48, mb: 2 }}>{isConfirmed ? "\u2705" : "\u23F3"}</Box>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: "var(--ink)" }}>
                {isConfirmed ? "Pagamento aprovado!" : "Aguardando confirmação do pagamento"}
              </Typography>
              <Typography sx={{ color: "var(--ink-soft)",  mb: 2, maxWidth: 360, mx: "auto" }}>
                {isConfirmed
                  ? "Seu pagamento foi aprovado. A confirmação final está sendo processada."
                  : "Sua solicitação de assinatura foi recebida. A confirmação pode levar alguns instantes."}
              </Typography>
              <Typography variant="body2" sx={{ color: "var(--ink-soft)" }}>
                Esta página será atualizada automaticamente quando o pagamento for confirmado.
              </Typography>
            </Box>
          );
        } else {
          content = (
            <Alert severity="warning">
              Dados de pagamento indisponíveis no momento.
              {paymentData.paymentLink && (
                <Button
                  size="small"
                  href={paymentData.paymentLink}
                  target="_blank"
                  sx={{ ml: 1 }}
                >
                  Abrir link externo
                </Button>
              )}
            </Alert>
          );
        }
        return (
          <>
            {paymentData.ajusteProporcional && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Esta cobrança corresponde apenas à diferença proporcional entre o plano
                anterior e o novo — o plano novo já está ativo. Não é uma nova assinatura.
              </Alert>
            )}
            {!paymentData.ajusteProporcional &&
              avisoPagamentoAteData &&
              paymentData.billingType !== "CREDIT_CARD" && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Você ainda está no período de trial gratuito — essa cobrança só vence em{" "}
                  {avisoPagamentoAteData}, então não precisa pagar agora. Você continua com
                  acesso normal ao sistema até lá.
                </Alert>
              )}
            {content}
          </>
        );
      }

      case "success":
        return (
          <SuccessView
            message={paymentData?.creditCardMessage || "Pagamento Confirmado! Sua assinatura já está ativa."}
            onClose={handleClose}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={step === "payment-display" || step === "success" ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        // Este app nao tem ThemeProvider do MUI, entao tokens tipo
        // "background.paper" sempre resolveriam pro tema claro. As CSS vars do
        // design system sao as unicas que acompanham o dark mode de verdade —
        // mesmo raciocinio ja documentado em ConfirmDeleteDialog.
        sx: {
          borderRadius: 2,
          padding: "8px 16px 16px 16px",
          bgcolor: "var(--surface)",
          color: "var(--ink)",
          border: "1px solid var(--border)",
          backgroundImage: "none",
        },
      }}
    >
      <DialogTitle
        sx={{
          textAlign: "center",
          pt: 3,
          pb: 1,
          px: 1,
          position: "relative",
        }}
      >
        <Typography
          variant="h6"
          component="span"
          sx={{ fontWeight: 600, display: "block" }}
        >
          {step === "select-method" && "Escolha a forma de pagamento"}
          {step === "payment-display" && "Aguardando Pagamento"}
          {step === "success" && (
            paymentData?.status === "ASSINATURA_REATIVADA"
              ? "Assinatura Reativada"
              : paymentData?.status === "PLANO_ALTERADO"
                ? "Plano Alterado"
                : "Pagamento Confirmado"
          )}
        </Typography>
        {step !== "payment-display" && step !== "success" && (
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent sx={{ overflowX: "hidden" }}>
        {renderStepIndicator()}
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
};

export default PagamentoModal;
